<template>
  <div id="headerDropdown" ref="target" class="c-dropdown" :data-open="dropdownOpen">
    <button class="c-dropdown__button" :aria-label="$t('HEADER.DROPDOWN_TOGGLE')" @click.prevent="onDropdownClick">
      <Avatar :user-name="userName" />
      <span class="c-dropdown__label">{{ t('HEADER.TITLE') }}</span>
      <DropdownArrowIcon class="c-dropdown__arrow" />
    </button>

    <div v-if="dropdownOpen" class="c-dropdown__menu">
      <span class="c-dropdown__user-name">{{ userName }}</span>

      <span class="c-dropdown__title">{{ t('HEADER.MAIN_LANGUAGE') }}</span>
      <span class="c-dropdown__subtitle">{{ t('HEADER.MAIN_LANGUAGE_SUBTEXT') }}</span>

      <LanguageSwitcher class="c-dropdown__language-radio" @change="closeDropdown" />

      <button class="c-dropdown__logout-btn" @click="openConfirmationModal" @keydown.enter="openConfirmationModal">
        {{ $t('LOGOUT') }}
      </button>

      <button class="c-dropdown__close-btn" @click="closeDropdown" @keydown.enter="closeDropdown">
        <CloseIcon class="c-dropdown__close-icon" />
        {{ $t('HEADER.CLOSE') }}
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { User } from '@/types/interface/User.interface';
import Avatar from '@/components/avatar/BaseAvatar.vue';
import { useAuth } from '@/core';
import DropdownArrowIcon from '@/assets/icons/DropdownArrowIcon.vue';
import CloseIcon from '@/assets/icons/CloseIcon.vue';
import useModal from '@/services/useModal';
import ConfirmationModal from '@/components/layout/modal/ConfirmationModal.vue';
import LanguageSwitcher from '@/components/language-switcher/LanguageSwitcher.vue';
import { useI18n } from 'vue-i18n';
import { prepareDisplayName } from '@/utils/user';

interface DropdownProps {
  user: User;
}

const props = defineProps<DropdownProps>();

const { t } = useI18n();
const { logout } = useAuth();
const modal = useModal();
const dropdownOpen = ref<boolean>(false);
const target = ref(null);

const userName = computed(() => prepareDisplayName(props.user));

const toggleDropdown = () => {
  dropdownOpen.value = !dropdownOpen.value;
};

const closeDropdown = () => {
  dropdownOpen.value = false;
};

const onDropdownClick = () => {
  toggleDropdown();
};

onClickOutside(target, () => {
  if (dropdownOpen.value) {
    closeDropdown();
  }
});

const openConfirmationModal = () => {
  closeDropdown();
  modal.open(
    ConfirmationModal,
    {
      title: t('MODAL.LOGOUT.TITLE'),
      text: t('MODAL.LOGOUT.TEXT'),
      cancelButton: t('BUTTON.CANCEL'),
      okButton: t('LOGOUT'),
    },
    {
      confirm: () => {
        logout();
      },
    },
  );
};
</script>
